import { useEffect, useMemo, useState } from "react";
import { tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp, LifeBuoy, Megaphone, HeartHandshake, Receipt, Crown, Bot,
  ShieldAlert, ShieldCheck, Gauge,
} from "lucide-react";

export const AGENT_ICON = {
  SALES: TrendingUp, SUPPORT: LifeBuoy, CAMPAIGN: Megaphone,
  RETENTION: HeartHandshake, COLLECTIONS: Receipt, EXECUTIVE: Crown,
};
export const AgentIcon = ({ code, className }) => {
  const Icon = AGENT_ICON[code] || Bot;
  return <Icon className={className} />;
};

export const RISK_STYLE = {
  high: "text-red-600 bg-red-500/10 border-red-500/30",
  medium: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  low: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
};
export const CONF_STYLE = {
  High: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  Medium: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  Low: "text-red-600 bg-red-500/10 border-red-500/30",
};

export function RiskBadge({ level }) {
  const lvl = (level || "low").toLowerCase();
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${RISK_STYLE[lvl] || RISK_STYLE.low}`} data-testid="risk-badge">
      {lvl === "high" ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
      {lvl} risk
    </span>
  );
}

export function ConfidenceBadge({ label, value }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${CONF_STYLE[label] || CONF_STYLE.Low}`} data-testid="confidence-badge">
      <Gauge className="h-3 w-3" /> {label}{value != null ? ` (${Math.round(value * 100)}%)` : ""}
    </span>
  );
}

export const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

/** Tenant scoping for SuperAdmin. Returns the controls + resolved tenantId (undefined for tenant users). */
export function useTenantScope() {
  const { user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  useEffect(() => {
    if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {});
  }, [isSuper]);
  const params = useMemo(() => (tenantId ? { tenantId } : {}), [tenantId]);
  return { isSuper, tenants, tenantId, setTenantId, params };
}

export function TenantPicker({ isSuper, tenants, tenantId, setTenantId }) {
  if (!isSuper) return null;
  return (
    <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
      className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
      data-testid="agent-tenant-select">
      <option value="">Select tenant…</option>
      {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
    </select>
  );
}

export function SelectTenantNotice() {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="select-tenant-notice">
      <Bot className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
      <h3 className="text-base font-semibold text-foreground">Select a tenant</h3>
      <p className="text-sm text-muted-foreground mt-1">Choose a tenant above to view its AI agents.</p>
    </div>
  );
}

export function PageHeader({ icon: Icon, title, subtitle, children }) {
  return (
    <header className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {Icon && <Icon className="h-5 w-5" />}
        </span>
        <div>
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </header>
  );
}
